import { useRef, useCallback } from "react";
import { useToast } from "@/hooks/use-toast";
import { Toast, ToastClose, ToastDescription, ToastProvider, ToastTitle, ToastViewport } from "@/components/ui/toast";
import { AVISO_MS } from "@/lib/avisoDuracion";

const SWIPE_THRESHOLD = 80;

/* Las duraciones viven en `@/lib/avisoDuracion`, compartidas con sonner: la
   aplicación tiene dos sistemas de avisos y no pueden ir cada uno por su lado.
   Ya no se distingue móvil de escritorio —eso no era lo que fallaba—, sino
   acierto de error. */

export function Toaster() {
  const { toasts, dismiss } = useToast();
  return (
    <ToastProvider duration={AVISO_MS.ok}>

      {toasts.map(function ({ id, title, description, action, ...props }) {
        return (
          <SwipeableToast key={id} id={id} dismiss={dismiss}>
            {/* `variant` sigue dentro de `props`: los errores duran más. */}
            <Toast {...props} duration={props.variant === "destructive" ? AVISO_MS.error : undefined}>
              <div className="grid gap-1">
                {title && <ToastTitle>{title}</ToastTitle>}
                {description && <ToastDescription>{description}</ToastDescription>}
              </div>
              {action}
              <ToastClose />
            </Toast>
          </SwipeableToast>
        );
      })}
      <ToastViewport />
    </ToastProvider>
  );
}

function SwipeableToast({
  id,
  dismiss,
  children,
}: {
  id: string;
  dismiss: (id: string) => void;
  children: React.ReactNode;
}) {
  const startX = useRef<number | null>(null);

  const onTouchStart = useCallback((e: React.TouchEvent) => {
    startX.current = e.touches[0].clientX;
  }, []);

  const onTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      if (startX.current === null) return;
      const delta = e.changedTouches[0].clientX - startX.current;
      startX.current = null;
      if (Math.abs(delta) > SWIPE_THRESHOLD) {
        dismiss(id);
      }
    },
    [id, dismiss],
  );

  return (
    <div onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
      {children}
    </div>
  );
}
